import { OVERLAY_DEFAULT_CONFIG, OverlayDefaultConfig } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorDefaultOptions,
  MatPaginatorModule,
} from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
  MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
  MatSlideToggleDefaultOptions,
  MatSlideToggleModule,
} from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService } from '@ngx-translate/core';
import { OAuthModule, OAuthService } from 'angular-oauth2-oidc';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { CookieService } from 'ngx-cookie-service';
import { AdminModule } from './admin/admin.module';
import { AuthRepository } from './repositories/auth.repository';
import { CustomInterceptorService } from './services/authentication/custom-interceptor.service';
import { SharedModule } from './shared/shared.module';
import { ZxComponentsModule } from './zx-components/zx-components.module';
import { ZxProjectsModule } from './zx-projects/zx-projects.module';

import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { AppRoutingModule } from './app-routing.module';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection(),
    provideTranslateService({
      loader: provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    }),
    provideCharts(withDefaultRegisterables()),
    importProvidersFrom(
      OAuthModule.forRoot({
        resourceServer: {
          sendAccessToken: false,
        },
      }),
      AppRoutingModule,
      MatProgressSpinnerModule,
      MatCardModule,
      AngularSvgIconModule.forRoot(),
      BrowserModule,
      SharedModule,
      AdminModule,
      ZxProjectsModule,
      ZxComponentsModule,
      FormsModule,
      MatButtonModule,
      MatIconModule,
      MatSelectModule,
      MatInputModule,
      MatSlideToggleModule,
      MatDividerModule,
      MatTreeModule,
      MatTooltipModule,
      MatMenuModule,
      MatSidenavModule,
      MatDialogModule,
      ReactiveFormsModule,
      MatStepperModule,
      MatSnackBarModule,
      MatPaginatorModule
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CustomInterceptorService,
      multi: true,
    },
    // The legacy fields reserved no space for hints/errors (styles.scss had `padding-bottom: 0` on the wrapper).
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
    },
    // The legacy paginator used an underline page-size field; 'fill' is restyled as an underline (theme.scss, form-field.scss).
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: { formFieldAppearance: 'fill' } as MatPaginatorDefaultOptions,
    },
    // Legacy slide toggles had no check / minus icon in the thumb.
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: { hideIcon: true } as MatSlideToggleDefaultOptions,
    },
    // Legacy tab links kept their own width; MDC stretches them across the bar by default.
    {
      provide: MAT_TABS_CONFIG,
      useValue: { stretchTabs: false } as MatTabsConfig,
    },
    // CDK 22 renders overlays as popovers next to their trigger by default, so panels inherit the trigger's styles
    // (e.g. the location autocomplete took its field's 12px font). Keep them in the body overlay container, as on v18.
    {
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false } as OverlayDefaultConfig,
    },
    // Material's default max-width is 80vw, which leaves phone dialogs 312px wide. Let them use the screen minus a margin.
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { maxWidth: 'calc(100vw - 24px)' } as MatDialogConfig,
    },
    AuthRepository,
    OAuthService,
    CookieService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAnimations(),
  ],
};
